import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import {
  PanelInstanceContext,
} from 'pages/Dashboard/contexts/sidePanel';

type PanelStateOptions<T> = {
  param: string;
  parse: (raw: string) => T | null;
  serialize: (value: T) => string;
  defaultValue: T;
  // Set to false to keep the param when it equals the default
  removeDefault?: boolean;
  // Local state only: the value to start from. In a linkable panel the URL holds it
  initialValue?: T;
};

/**
 * State for a side panel, such as its active tab. In a linkable panel the
 * value lives in a URL param the panel declares; elsewhere it is local state.
 * The setter does nothing once the component has unmounted.
 */
export default function usePanelState<T>({
  param,
  parse,
  serialize,
  defaultValue,
  removeDefault = true,
  initialValue,
}: PanelStateOptions<T>): [T, (value: T) => void] {
  const panelInstance = useContext(PanelInstanceContext);

  if (!isNil(panelInstance) && !panelInstance.params.includes(param)) {
    throw new Error(`usePanelState: the panel does not declare the param "${param}"`);
  }

  const [localValue, setLocalValue] = useState<T>(initialValue ?? defaultValue);
  const raw = isNil(panelInstance)
    ? null
    : new URLSearchParams(panelInstance.search).get(param);
  const urlValue = useMemo(() => (isNil(raw) ? null : parse(raw)), [raw]);

  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const latestRef = useRef({ panelInstance, serialize, defaultValue });
  latestRef.current = { panelInstance, serialize, defaultValue };

  const setValue = useCallback((value: T) => {
    if (!isMountedRef.current) {
      return;
    }

    const { panelInstance: instance, serialize: toParam, defaultValue: fallback } = latestRef.current;
    if (isNil(instance)) {
      setLocalValue(value);
      return;
    }

    const serialized = toParam(value);
    const shouldRemove = removeDefault && serialized === toParam(fallback);
    instance.queueUrlParamsUpdate((searchParams) => {
      if (shouldRemove) {
        searchParams.delete(param);
      } else {
        searchParams.set(param, serialized);
      }
    });
  }, [param, removeDefault]);

  if (isNil(panelInstance)) {
    return [localValue, setValue];
  }
  return [urlValue ?? defaultValue, setValue];
}
